import React, { useEffect, useState } from 'react';

import { BrowserProfilePicker } from '../../components/BrowserProfilePicker';
import { errorMessage } from '../../services/apiError';
import { confirmAppDialog } from '../../services/appDialogs';
import axiosInstance from '../../services/axiosInstance';
import { forgetLoopBrowser } from '../../services/extensionBridge';
import { loopKeyFingerprint } from '../../services/loopEncryption';
import { loopService } from '../../services/loopService';
import type { BrowserProfile } from '../../services/loopService';

import { isCurrentLoopBrowser, LOOP_RECOVERY_UPDATE_MESSAGE, LoopBrowserPairing } from './LoopBrowserPairing';
import { LoopConnectionTest } from './LoopConnectionTest';
import { useLoopBrowserExtension } from './useLoopBrowserExtension';
import type { LoopsState } from './useLoops';

export const loopDate = (value?: string | null): string => (
  value ? new Date(value).toLocaleString() : 'Not yet'
);

export const BrowserSelect: React.FC<{
  profiles: BrowserProfile[]; value: string; onChange: (value: string) => void; disabled?: boolean;
}> = ({ profiles, value, onChange, disabled }) => (
  <div className="loop-field">
    <span>Browser profile</span>
    <BrowserProfilePicker profiles={profiles} value={value} onChange={onChange} disabled={disabled} />
  </div>
);

export const downloadExtension = async () => {
  const response = await axiosInstance.get('/api/v1/loops/extension/download', { responseType: 'blob' });
  const url = URL.createObjectURL(response.data);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = '8080-browser-extension.zip';
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const ExtensionGuide: React.FC<{ state: LoopsState; latest?: string | null }> = ({ state, latest }) => (
  <section className="loop-card loop-install">
    <i className="fa-solid fa-puzzle-piece" aria-hidden="true" />
    <h3>Install the browser extension</h3>
    <p>Install the 8080.AI extension in whichever browser profile should run your loops.</p>
    <p className="loop-muted">{latest ? `The download is version ${latest}.`
      : 'The download is always the latest version, with browser pairing and live previews.'}</p>
    <ol>
      <li>Use a Chromium browser such as Chrome or Edge.</li>
      <li><button className="loop-link" onClick={() => void state.perform(downloadExtension)}>
        Download the extension</button>, then unzip it.</li>
      <li>Open your browser’s extensions page, enable Developer mode, and choose Load unpacked.</li>
      <li>Select the extension folder to finish installing.</li>
      <li>For this browser, refresh this page and choose <strong>Connect this browser</strong>.
        For a different browser, choose <strong>Another browser</strong> above and enter its code in that browser’s
        extension settings.</li>
    </ol>
    <p className="loop-muted">Already installed? Finish any active runs, download the update and replace the files
      in the extension’s existing folder. Click Reload for 8080.AI on your browser’s extensions page,
      then refresh this page.</p>
    <p className="loop-muted">Repeat for each profile or computer. Keep that browser open while loops run.</p>
  </section>
);

const KeyFingerprint: React.FC<{ profile: BrowserProfile }> = ({ profile }) => {
  const [fingerprint, setFingerprint] = useState('');

  useEffect(() => {
    let active = true;

    loopKeyFingerprint(profile.public_key).then((value) => { if (active) setFingerprint(value); })
      .catch(() => { if (active) setFingerprint('Unavailable'); });

    return () => { active = false; };
  }, [profile.public_key]);

  return <details className="loop-fingerprint"><summary>Verify encryption key</summary>
    <p>Compare this fingerprint with the one shown in this profile’s extension settings.</p>
    <code>{fingerprint || 'Calculating…'}</code>
  </details>;
};

// eslint-disable-next-line max-lines-per-function, max-statements
export const LoopBrowsers: React.FC<{ state: LoopsState }> = ({ state }) => {
  const browser = useLoopBrowserExtension();
  const [revokedHere, setRevokedHere] = useState<BrowserProfile[]>([]);
  const [disconnectError, setDisconnectError] = useState('');
  const profiles = state.profiles.filter((profile) => !revokedHere.some((revoked) => revoked.id === profile.id));
  const revokedProfiles = [...(state.revokedProfiles || []), ...revokedHere];
  const cleanupPending = !browser.extension
    || revokedProfiles.some((profile) => isCurrentLoopBrowser(browser.extension, profile.id));
  // The account revocation must complete even if an offline or older extension cannot clear its local pairing.
  // eslint-disable-next-line max-statements
  const disconnect = async (profile: BrowserProfile) => {
    setDisconnectError('');
    await loopService.revoke(profile.id);
    setRevokedHere((previous) => [...previous.filter((item) => item.id !== profile.id), { ...profile, status: 'revoked' }]);
    try {
      const current = await browser.refreshExtension();

      if (!current && isCurrentLoopBrowser(browser.extension, profile.id)) {
        throw new Error('The extension is unavailable. Refresh this page, then click Connect this browser to try again.');
      }
      if (!current || !isCurrentLoopBrowser(current, profile.id)) return;
      if (!current.revoked_pairing_recovery) throw new Error(LOOP_RECOVERY_UPDATE_MESSAGE);
      await forgetLoopBrowser({ profileId: profile.id, apiOrigin: current.api_origin });
    } catch (reason) {
      setDisconnectError(`The browser was disconnected from your account, but its local connection still needs attention. ${errorMessage(reason)}`);
    } finally { await browser.refreshExtension(); }
  };
  const confirmDisconnect = async (profile: BrowserProfile) => {
    if (!await confirmAppDialog({
      title: 'Disconnect browser?',
      message: `“${profile.name}” will be disconnected. Its loops will pause until you reconnect and resume them.`,
      confirmLabel: 'Disconnect', confirmVariant: 'danger',
    })) return;
    await state.perform(() => disconnect(profile));
  };

  return <div className="loop-panel-content">
    <h2>Your browsers</h2>
    <p className="loop-muted">Connect this browser directly, or use a pairing code to connect another browser or device.</p>
    <LoopBrowserPairing state={{ ...state, profiles, revokedProfiles }} {...browser} />
    {disconnectError && cleanupPending && <p className="loop-error" role="alert">{disconnectError}</p>}
    {profiles.length > 0 && <h3 className="loop-browser-list-title">Saved browser profiles</h3>}
    {profiles.map((profile) => <section className="loop-card loop-saved-browser" key={profile.id}>
      <div className="loop-row"><h3>{profile.name}</h3>
        <span className={`loop-status is-${profile.status}`}>{profile.status}</span></div>
      <span className={`loop-browser-location${isCurrentLoopBrowser(browser.extension, profile.id) ? ' is-current' : ''}`}>
        {isCurrentLoopBrowser(browser.extension, profile.id) ? 'This browser'
          : browser.extension ? 'Another browser or profile' : 'Saved browser profile'}
      </span>
      <p className="loop-muted">Last connected: {loopDate(profile.last_seen_at)}</p>
      <LoopConnectionTest profile={profile} disabled={state.busy} />
      <KeyFingerprint profile={profile} />
      {profile.parallel_runs_supported === false && <p className="loop-muted" role="status">
        Update this extension to run multiple loops at once. Finish current runs before reloading.{' '}
        <button className="loop-link" disabled={state.busy} onClick={() => void state.perform(downloadExtension)}>
          Download update</button>
      </p>}
      <button disabled={state.busy} onClick={() => void confirmDisconnect(profile)}>Disconnect</button>
    </section>)}
    <ExtensionGuide state={state} />
  </div>;
};
